import React from "react";
import { Link } from "react-router-dom";
import IndiaFooterNotice from "./IndiaFooterNotice";

type NavItem = { name: string; href: string };
type SocialItem = { name: string; href: string; icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element };

// Bump this string whenever the marketing copy changes meaningfully so AI
// engines and Google Discover can detect freshness signals on the SPA. It
// surfaces in the visual footer ("Last updated …") and as a hidden
// `<meta itemprop="dateModified">` so structured-data parsers pick it up
// even though the React app never re-renders the document <head>.
const SITE_LAST_UPDATED_ISO = "2026-10-16";
const SITE_LAST_UPDATED_LABEL = "Oct 16, 2026";

const isInternalHref = (href: string) => href.startsWith("/") && !href.startsWith("//");

const navigation: {
    product: NavItem[];
    legal: NavItem[];
    social: SocialItem[];
} = {
    product: [
        { name: "Chat Widget", href: "/ai-chat-widget" },
        { name: "Knowledge Base", href: "/ai-knowledge-base" },
        { name: "Workflow Builder", href: "/ai-workflow-builder" },
        { name: "Voice Agents", href: "/ai-voice-agents" },
        { name: "Call Pipeline", href: "/ai-call-pipeline" },
        { name: "Outbound Triggers", href: "/ai-outbound-triggers" },
        { name: "CRM", href: "/ai-crm" },
        { name: "Sales Call Pipeline", href: "/sales-call-pipeline" },
        { name: "Managed Support", href: "/managed-support" },
        { name: "Shopify AI Support", href: "/shopify-ai-customer-support" },
        { name: "Documentation", href: "/docs" },
        { name: "Shopify Integration Docs", href: "/docs/shopify" },
        { name: "How it Works", href: "/#how-it-works" },
        { name: "Use Cases by Industry", href: "/use-cases" },
        { name: "Case Studies", href: "/case-studies" },
        { name: "Compare", href: "/compare" },
        { name: "Alternatives", href: "/alternatives" },
        { name: "Pricing", href: "/pricing" },
        { name: "Blog", href: "/blogs" },
    ],
    legal: [
        { name: "Privacy Policy", href: "/privacy" },
        { name: "Terms of Service", href: "/terms" },
        { name: "Cookie Policy", href: "/cookies" },
        { name: "Contact Us", href: "/contact" },
    ],
    social: [
        {
            name: "Instagram",
            href: "https://www.instagram.com/flogpt.ai/",
            icon: (props: React.SVGProps<SVGSVGElement>) => (
                <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
                    <path
                        fillRule="evenodd"
                        d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                        clipRule="evenodd"
                    />
                </svg>
            ),
        },
        {
            name: "LinkedIn",
            href: "https://www.linkedin.com/company/110911552",
            icon: (props: React.SVGProps<SVGSVGElement>) => (
                <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
            ),
        },
        {
            name: "Facebook",
            href: "https://www.facebook.com/people/Flogpt/61587978609007/",
            icon: (props: React.SVGProps<SVGSVGElement>) => (
                <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
                    <path
                        fillRule="evenodd"
                        d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                        clipRule="evenodd"
                    />
                </svg>
            ),
        },
    ],
};

export default function Footer() {
    return (
        <footer className="relative bg-slate-900/95 border-t border-white/10" aria-labelledby="footer-heading">
            <div className="pointer-events-none absolute inset-0 -z-10">
                <div className="absolute -left-24 top-10 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />
                <div className="absolute -right-24 bottom-10 h-64 w-64 rounded-full bg-purple-500/10 blur-3xl" />
            </div>
            <h2 id="footer-heading" className="sr-only">
                Footer
            </h2>
            <div className="mx-auto max-w-7xl px-6 pb-10 pt-16 sm:pt-24 lg:px-8 lg:pt-32">
                <div className="xl:grid xl:grid-cols-3 xl:gap-8">
                    <div className="space-y-8">
                        <div className="flex items-center space-x-3">
                            <img
                                src="/images/flogpt-iso.png"
                                alt="FloGPT"
                                width={48}
                                height={48}
                                loading="lazy"
                                decoding="async"
                                className="h-10 w-10 object-contain"
                            />
                            <span className="text-2xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">FloGPT</span>
                        </div>
                        <p className="text-sm leading-6 text-slate-300">Build intelligent chat agents with no code. Transform your customer experience with AI-powered conversations.</p>
                        <div className="flex space-x-6">
                            {navigation.social.map((item) => (
                                <a
                                    key={item.name}
                                    href={item.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-slate-400 hover:text-white transition-colors"
                                >
                                    <span className="sr-only">{item.name}</span>
                                    <item.icon className="h-6 w-6" aria-hidden="true" />
                                </a>
                            ))}
                        </div>
                    </div>
                    <div className="mt-16 grid grid-cols-1 gap-8 xl:col-span-2 xl:mt-0">
                        <div className="md:grid md:grid-cols-2 md:gap-8">
                            <div>
                                <h3 className="text-sm font-semibold leading-6 text-white">Legal</h3>
                                <ul className="mt-6 space-y-4">
                                    {navigation.legal.map((item) => (
                                        <li key={item.name}>
                                            {isInternalHref(item.href) ? (
                                                <Link to={item.href} className="text-sm leading-6 text-slate-300 hover:text-white transition-colors">
                                                    {item.name}
                                                </Link>
                                            ) : (
                                                <a href={item.href} className="text-sm leading-6 text-slate-300 hover:text-white transition-colors">
                                                    {item.name}
                                                </a>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold leading-6 text-white">Product</h3>
                                <ul className="mt-6 space-y-4">
                                    {navigation.product.map((item) => (
                                        <li key={item.name}>
                                            {isInternalHref(item.href) ? (
                                                <Link to={item.href} className="text-sm leading-6 text-slate-300 hover:text-white transition-colors">
                                                    {item.name}
                                                </Link>
                                            ) : (
                                                <a href={item.href} className="text-sm leading-6 text-slate-300 hover:text-white transition-colors">
                                                    {item.name}
                                                </a>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mt-16 border-t border-white/10 pt-8 sm:mt-20 lg:mt-24">
                    <div className="flex flex-col items-center justify-between sm:flex-row">
                        <div className="flex flex-col items-center sm:items-start">
                            <p className="text-xs leading-5 text-slate-400">
                                &copy; {new Date().getFullYear()} FloGPT. All rights reserved.
                            </p>
                            <p className="mt-1 text-[11px] leading-5 text-slate-500">
                                Last updated{" "}
                                <time dateTime={SITE_LAST_UPDATED_ISO}>{SITE_LAST_UPDATED_LABEL}</time>
                            </p>
                            <IndiaFooterNotice />
                            {/* Hidden machine-readable freshness signal — itemprop is
                                used by structured-data parsers (Google's Rich Results
                                test, Bing) to detect dateModified on pages that don't
                                ship a full Article schema (most marketing pages). */}
                            <meta itemProp="dateModified" content={SITE_LAST_UPDATED_ISO} />
                        </div>
                        <div className="mt-4 flex items-center space-x-4 sm:mt-0">
                            <span className="inline-flex items-center rounded-full bg-emerald-100/10 px-2 py-1 text-xs font-medium text-emerald-300 ring-1 ring-emerald-300/20">
                                <div className="mr-1 h-1.5 w-1.5 rounded-full bg-emerald-400"></div>
                                All systems operational
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
